import { useEffect, useMemo } from 'react'

import { useAddressContext } from '@/context/AddressContext'
import { useAuthContext } from '@/context/AuthContext'
import { useOperationalModelContext } from '@/context/OperationalModelContext'
import { useStoreContext } from '@/context/StoreContext'
import { useCartContext } from '@mercadoni/sinister/dist/cjs/cart'

import { useHandPicking } from '@mercadoni/sinister/dist/cjs/shared/contexts'
import useCartStatusWatcher from './useCartStatusWatcher'
import formatOperationalModelCart from './useCase/formatOperationalModelCart'
import initializeCart from './useCase/initializeCart'

import { ClientModel } from '@mercadoni/sinister/dist/lib/src/entities/clientModel'
import { DeadpoolUser, ProductCatalog } from '~types/index'
import shouldUpdateLoggedCart from './useCase/shouldUpdateLoggedCart'
import updateIsMigrableCart, {
  UpdateIsMigrableCartProps,
} from './useCase/updateIsMigrableCart'

interface AuthContextResponse {
  customer: DeadpoolUser | null
  isInitialized: boolean
}

const MiddleWare = ({
  clientModel,
  children,
  onAddProductHandPicking,
}: {
  children: React.ReactNode
  clientModel: ClientModel
  onAddProductHandPicking: (product: ProductCatalog) => void
}) => {
  const { customer, isInitialized }: AuthContextResponse = useAuthContext()
  const { store } = useStoreContext()
  const { operationalModel } = useOperationalModelContext()
  const { address } = useAddressContext()
  const {
    cart,
    addProduct,
    getProduct,
    type,
    initializer,
    restart,
    cartId,
    products,
  } = useCartContext()

  useHandPicking({ cart, addProduct, getProduct, onAddProductHandPicking })
  useCartStatusWatcher()

  const shouldInitialize = useMemo(
    () => isInitialized && !!store && !!operationalModel.opsModel,
    [isInitialized, store, operationalModel]
  )

  const baseParams = useMemo(
    () => ({
      clientId: clientModel.clientId as string,
      storeReference: store?.reference || store?.code,
      addressId: address?.id,
      operationalModel:
        operationalModel?.opsModel &&
        formatOperationalModelCart(operationalModel.opsModel),
    }),
    [clientModel, store, address, operationalModel]
  )

  const updateLoggedCart = async () => {
    await restart(baseParams)
  }

  const startInitiliazeCart = async () => {
    await initializeCart({
      baseParams,
      customer,
      cartId,
      initializer,
      restart,
    })
  }

  useEffect(() => {
    const params: UpdateIsMigrableCartProps = {
      shouldInitialize,
      customer,
      products,
      type,
      cartId,
    }

    updateIsMigrableCart(params)
  }, [products, cartId, type, customer, shouldInitialize])

  useEffect(() => {
    if (shouldInitialize) {
      startInitiliazeCart()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldInitialize, store, operationalModel, customer, address])

  useEffect(() => {
    if (shouldUpdateLoggedCart({ cart, address, operationalModel })) {
      updateLoggedCart()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart])

  return children
}

export default MiddleWare
